import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, Card, CardContent, CardHeader, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import {
  BooleanInput,
  Button,
  Datagrid,
  DeleteButton,
  Edit,
  FunctionField,
  ReferenceManyField,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useInput,
  useRecordContext,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { IconPicker } from '../../components/IconPicker';
import { CommandParameterDialog } from './CommandParameterDialog';

// Wrapper to make IconPicker compatible with React Admin
const IconPickerInput = (props: any) => {
  const { field } = useInput(props);
  return <IconPicker {...field} label={props.label} helperText={props.helperText} />;
};

export const CommandEdit = () => {
  return (
    <Edit>
      <SimpleForm>
        <TextInput source="name" validate={[required()]} fullWidth />
        <TextInput source="display_name" validate={[required()]} fullWidth label="Display Name" />
        <TextInput source="description" validate={[required()]} multiline rows={3} fullWidth />
        <SelectInput
          source="category"
          choices={[
            { id: 'system', name: 'System' },
            { id: 'application', name: 'Application' },
            { id: 'network', name: 'Network' },
            { id: 'diagnostics', name: 'Diagnostics' },
          ]}
          validate={[required()]}
        />
        <SelectInput
          source="danger_level"
          choices={[
            { id: 'low', name: 'Low' },
            { id: 'medium', name: 'Medium' },
            { id: 'high', name: 'High' },
            { id: 'critical', name: 'Critical' },
          ]}
          validate={[required()]}
        />
        <IconPickerInput source="icon" label="Icon" helperText="Select an icon for this command" />
        <TextInput
          source="mqtt_topic_pattern"
          validate={[required()]}
          fullWidth
          label="MQTT Topic Pattern"
          helperText="e.g., CMD/{hostname}/{command}"
        />
        <BooleanInput source="is_active" />
        <BooleanInput source="confirmation_required" label="Confirmation Required" />

        {/* Feedback Configuration Section */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Feedback Configuration
          </Typography>
          <BooleanInput
            source="feedback"
            label="Enable Real-time Feedback"
            helperText="Monitor command execution and show real-time output"
          />
        </Box>

        <FeedbackConfigFields />

        <ParametersReferenceManyField />
      </SimpleForm>
    </Edit>
  );
};

// Separate component to use useWatch for conditional rendering
const FeedbackConfigFields = () => {
  const feedbackEnabled = useWatch({ name: 'feedback' });
  const waitType = useWatch({ name: 'feedback_config.wait_type' });

  if (!feedbackEnabled) return null;

  return (
    <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main', mb: 2 }}>
      <SelectInput
        source="feedback_config.wait_type"
        label="Wait For"
        choices={[
          {
            id: 'done_message',
            name: 'Done Message on CMD_ACK/{hostname}',
          },
          {
            id: 'topic_message',
            name: 'Custom Topic/Message',
          },
        ]}
        validate={feedbackEnabled ? [required()] : []}
        helperText="What should the system wait for to confirm command completion?"
      />

      {waitType === 'topic_message' && (
        <>
          <TextInput
            source="feedback_config.custom_topic"
            label="Custom Topic Pattern"
            fullWidth
            helperText="e.g., UP/{hostname} or STATUS/{hostname}"
            validate={waitType === 'topic_message' ? [required()] : []}
          />
          <TextInput
            source="feedback_config.custom_message"
            label="Expected Message/Payload"
            fullWidth
            multiline
            rows={2}
            helperText='JSON payload to wait for, e.g., {"status":"ready"} or use {command} placeholder'
          />
        </>
      )}

      <BooleanInput
        source="feedback_config.show_terminal"
        label="Show Terminal Output"
        helperText="Display real-time command output in a terminal window"
      />
    </Box>
  );
};

const ParametersReferenceManyField = () => {
  const record = useRecordContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editParameter, setEditParameter] = useState<any>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');

  if (!record?.id) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>Save the command first to add parameters.</Box>
    );
  }

  const handleCreate = () => {
    setDialogMode('create');
    setEditParameter(null);
    setDialogOpen(true);
  };

  const handleEdit = (parameter: any) => {
    setDialogMode('edit');
    setEditParameter(parameter);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditParameter(null);
  };

  return (
    <>
      <ReferenceManyField
        reference={`commands/${record?.id}/parameters`}
        target="command_id"
        label="Parameters"
        fullWidth
      >
        <Card variant="outlined" sx={{ mt: 2, p: 2, width: '100%' }}>
          <CardHeader sx={{ p: 0, mb: 2 }} title={<Typography variant="h6">Parameters</Typography>} />
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Button onClick={handleCreate} variant="contained" startIcon={<AddIcon />}>
                Add Parameter
              </Button>
            </Box>
            <Datagrid bulkActionButtons={false} rowClick={false} empty={<Typography>No parameters defined</Typography>}>
              <TextField source="name" />
              <TextField source="display_name" />
              <TextField source="data_type" />
              <TextField source="ui_component" />
              <TextField source="is_required" label="Required" />
              <TextField source="order" />
              <FunctionField
                label="Actions"
                render={(param: any) => (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(param);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              />
              <DeleteButton
                mutationMode="pessimistic"
                confirmTitle="Delete Parameter"
                confirmContent="Are you sure you want to delete this parameter?"
                redirect={false}
              />
            </Datagrid>
          </CardContent>
        </Card>
      </ReferenceManyField>

      <CommandParameterDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        commandId={record.id}
        parameter={editParameter}
        mode={dialogMode}
      />
    </>
  );
};

// export const __CommandEdit = () => (
//   <Edit>
//     <SimpleForm>
//       <TextInput source="name" validate={[required()]} fullWidth />
//       <TextInput source="description" validate={[required()]} multiline rows={3} fullWidth />
//       <SelectInput
//         source="category"
//         choices={[
//           { id: 'system', name: 'System' },
//           { id: 'application', name: 'Application' },
//           { id: 'network', name: 'Network' },
//           { id: 'diagnostics', name: 'Diagnostics' },
//         ]}
//         validate={[required()]}
//       />
//       <SelectInput
//         source="danger_level"
//         choices={[
//           { id: 'low', name: 'Low' },
//           { id: 'medium', name: 'Medium' },
//           { id: 'high', name: 'High' },
//           { id: 'critical', name: 'Critical' },
//         ]}
//         validate={[required()]}
//       />
//       <BooleanInput source="is_active" />
//       <BooleanInput source="confirmation_required" />

//       <ArrayInput source="parameters">
//         <SimpleFormIterator>
//           <TextInput source="name" validate={[required()]} />
//           <TextInput source="display_name" validate={[required()]} />
//           <TextInput source="description" multiline rows={2} fullWidth />
//           <SelectInput
//             source="data_type"
//             choices={[
//               { id: 'string', name: 'String' },
//               { id: 'integer', name: 'Integer' },
//               { id: 'boolean', name: 'Boolean' },
//               { id: 'array', name: 'Array' },
//               { id: 'object', name: 'Object' },
//             ]}
//             validate={[required()]}
//           />
//           <SelectInput
//             source="ui_component"
//             choices={[
//               { id: 'text', name: 'Text Input' },
//               { id: 'textarea', name: 'Text Area' },
//               { id: 'number', name: 'Number Input' },
//               { id: 'select', name: 'Select (Single)' },
//               { id: 'multiselect', name: 'Multi-Select' },
//               { id: 'checkbox', name: 'Checkbox' },
//               { id: 'radio', name: 'Radio Buttons' },
//             ]}
//             validate={[required()]}
//           />
//           <BooleanInput source="is_required" />
//           <NumberInput source="order" validate={[required()]} />
//         </SimpleFormIterator>
//       </ArrayInput>
//     </SimpleForm>
//   </Edit>
// );
